/**
 * Cloud Sync Service
 * Calls the export-worker /sync/* endpoints. The sync state itself is read
 * live from Firestore (cloudSync/{uid}, see useCloudSync) — these calls only
 * trigger transitions.
 */

import { workerPost, type CloudProviderId } from "./cloudAccountsService";

// Mirror of the worker's SyncConfig (cloudSync/{uid} document)
export type SyncStatus = "active" | "paused" | "disabled";
export type SyncPauseReason = "user" | "quota_full" | "access_revoked" | "provider_error";
export type SyncSelection = Record<string, "all" | string[]>;

export interface SyncViewCursor {
  capturedAt: string;
  photoId: string;
  done: boolean;
}

export interface SyncConfig {
  provider: CloudProviderId;
  status: SyncStatus;
  pauseReason: SyncPauseReason | null;
  selection: SyncSelection;
  backfill: {
    status: "idle" | "running" | "done";
    cursors: Record<string, SyncViewCursor>;
    queueGeneration: number;
    lastPumpAt?: string;
  };
  counters: {
    selectedPhotos: number;
    syncedPhotos: number;
    failedPhotos: number;
    bytesSynced: number;
  };
  quota: { totalBytes: number | null; usedBytes: number; fetchedAt: string } | null;
  folders: Record<string, { folderId: string; folderWebUrl: string | null; timeZone: string }>;
  createdAt: string;
  updatedAt: string;
  lastSyncAt: string | null;
  lastReconcileAt?: string;
  /** Firestore Timestamp, server-set ONLY on real uploads (not on
   * verifications) — drives the Uploading vs Checking state. */
  lastUploadAt?: { toDate: () => Date };
}

/** Enable (or reactivate) the sync on a connected provider. */
export async function enableSync(
  provider: CloudProviderId,
  idToken: string
): Promise<SyncConfig> {
  const { config } = await workerPost<{ config: SyncConfig }>("/sync/enable", idToken, {
    provider,
  });
  return config;
}

/** Replace the project/view selection. */
export async function updateSyncSelection(
  selection: SyncSelection,
  idToken: string
): Promise<{ selection: SyncSelection; selectedPhotos: number }> {
  return workerPost("/sync/selection", idToken, { selection });
}

export async function pauseSync(idToken: string): Promise<void> {
  await workerPost<{ success: boolean }>("/sync/pause", idToken, {});
}

export async function resumeSync(idToken: string): Promise<void> {
  await workerPost<{ success: boolean }>("/sync/resume", idToken, {});
}

export async function disableSync(idToken: string): Promise<void> {
  await workerPost<{ success: boolean }>("/sync/disable", idToken, {});
}

/** Integrity check: re-walk the whole selection to recover any lost photo. */
export async function reconcileSync(idToken: string): Promise<void> {
  await workerPost<{ success: boolean }>("/sync/reconcile", idToken, {});
}
